
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import toast from "react-hot-toast";
import { Box, Heading, Flex, Spinner, Button, Table, Thead, Tbody, Tr, Th, Td, useColorModeValue, Select, Input } from "@chakra-ui/react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
function AdminKitchenDashboard() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState({});
  const boxBg = useColorModeValue("white", "gray.800");

  const fetchUsers = useCallback(async () => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/all-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      toast.error("Error loading kitchen data");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { if (user) fetchUsers(); }, [fetchUsers, user]);

  const handleStatusChange = async (userId, status) => {
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/update-meal-status/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      toast.success("Status updated!");
      fetchUsers();
    } catch (err) {
      toast.error("Could not update status");
    }
  };

  const handleUpload = async (userId) => {
    const file = selectedFiles[userId];
    if (!file) return toast.error("No file selected");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/upload-genome-file`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      toast.success("Genome uploaded!");
      fetchUsers();
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const exportToCSV = () => {
    if (!users || users.length === 0) return;
    const csvRows = [
      ["Email", "Meal Plan Status", "Genome Status", "Uploaded By", "Results File"],
      ...users.map((u) => [
        u.email,
        u.mealPlanStatus || "N/A",
        u.genome?.status || "N/A",
        u.genome?.uploadedBy || "N/A",
        u.genome?.resultsFileUrl || "",
      ]),
    ];
    const csvContent = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "serena_kitchen_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box maxW="7xl" mx="auto" p={6}>
      <Heading mb={6} textAlign="center" color="teal.600">Kitchen Dashboard</Heading>
      <Button colorScheme="teal" mb={4} onClick={exportToCSV}>Export to CSV</Button>
      {loading ? (
        <Flex justify="center" align="center" minH="200px"><Spinner size="xl" /></Flex>
      ) : (
        <Box bg={boxBg} p={6} borderRadius="lg" boxShadow="md">
          <Table variant="striped" colorScheme="teal" size="sm">
            <Thead>
              <Tr>
                <Th>Email</Th>
                <Th>Meal Plan Status</Th>
                <Th>Genome Status</Th>
                <Th>Upload Genome</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((u) => (
                <Tr key={u.userId}>
                  <Td>{u.email}</Td>
                  <Td>
                    <Select size="sm" value={u.mealPlanStatus || "Pending"} onChange={e => handleStatusChange(u.userId, e.target.value)}>
                      <option value="Pending">Pending</option>
                      <option value="Preparing">Preparing</option>
                      <option value="Delivered">Delivered</option>
                    </Select>
                  </Td>
                  <Td>{u.genome?.status || "N/A"}</Td>
                  <Td>
                    <Input type="file" size="sm" onChange={e => setSelectedFiles({ ...selectedFiles, [u.userId]: e.target.files[0] })} mb={2} />
                    <Button size="xs" colorScheme="green" onClick={() => handleUpload(u.userId)}>Upload Genome</Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default AdminKitchenDashboard;